import React, { useState, useEffect, Suspense, lazy } from 'react'
import { BrowserRouter, Routes, Route, NavLink, Navigate } from 'react-router-dom'
import { Toaster } from 'react-hot-toast'
import {
  LayoutDashboard, ListOrdered, BrainCircuit, BarChart3,
  Upload, Settings, Cpu, LogOut, ChevronDown,
  CreditCard, RefreshCw, User, Menu, X
} from 'lucide-react'
import { getStatus } from './utils/api'
import { AuthProvider, useAuth } from './context/AuthContext'
import { AccountProvider } from './context/AccountContext'
import EdgelogbookLogo from './components/Edgeloglogo'

const Dashboard = lazy(() => import('./pages/Dashboard'))
const Trades = lazy(() => import('./pages/Trades'))
const TradeDetail = lazy(() => import('./pages/TradeDetail'))
const AICoach = lazy(() => import('./pages/AICoach'))
const Analytics = lazy(() => import('./pages/Analytics'))
const ImportPage = lazy(() => import('./pages/ImportPage'))
const SettingsPage = lazy(() => import('./pages/SettingsPage'))
const BillingPage = lazy(() => import('./pages/Billingpage'))
const SyncPage = lazy(() => import('./pages/Syncpage'))
const ProfilePage = lazy(() => import('./pages/Profilepage'))
const LoginPage = lazy(() => import('./pages/LoginPage'))
const RegisterPage = lazy(() => import('./pages/RegisterPage'))
const VerifyEmailPage = lazy(() => import('./pages/VerifyEmailPage'))

const PLAN_BADGE = {
  free:  { label: 'FREE',  bg: 'rgba(74,85,104,0.2)',       color: 'var(--text-muted)' },
  pro:   { label: 'PRO',   bg: 'rgba(125,211,252,0.12)',    color: 'var(--text-accent)' },
  edge:  { label: 'EDGE',  bg: 'rgba(16,217,138,0.12)',     color: 'var(--green)' },
}

function Sidebar({ appStatus, isOpen, onClose }) {
  const { logout, user } = useAuth()
  const [showUserMenu, setShowUserMenu] = useState(false)

  const handleNavClick = () => {
    if (window.innerWidth < 768) onClose()
  }

  const plan  = user?.tier || user?.subscription_tier || 'free'
  const badge = PLAN_BADGE[plan] || PLAN_BADGE.free

  return (
    <aside className={`sidebar${isOpen ? ' sidebar-open' : ''}`}>
      <div className="sidebar-logo">
        <EdgelogbookLogo size="sm" showTagline />
        <button className="sidebar-close-btn" onClick={onClose} aria-label="Close menu">
          <X size={18} />
        </button>
      </div>

      <nav className="sidebar-nav">
        <span className="nav-section-label">Main</span>
        <NavLink to="/dashboard" onClick={handleNavClick} className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
          <LayoutDashboard size={15} /> Dashboard
        </NavLink>
        <NavLink to="/trades" onClick={handleNavClick} className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
          <ListOrdered size={15} /> Trades
        </NavLink>
        <NavLink to="/analytics" onClick={handleNavClick} className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
          <BarChart3 size={15} /> Analytics
        </NavLink>

        <span className="nav-section-label" style={{ marginTop: 8 }}>Tools</span>
        <NavLink to="/ai" onClick={handleNavClick} className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
          <BrainCircuit size={15} /> AI Coach
        </NavLink>
        <NavLink to="/import" onClick={handleNavClick} className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
          <Upload size={15} /> Import Trades
        </NavLink>
        <NavLink to="/sync" onClick={handleNavClick} className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
          <RefreshCw size={15} /> Auto-Sync
          {plan === 'free' && (
            <span style={{
              marginLeft: 'auto', fontSize: 8, padding: '1px 5px', borderRadius: 4,
              background: 'rgba(125,211,252,0.12)', color: 'var(--text-accent)',
              fontFamily: 'var(--font-mono)',
            }}>PRO</span>
          )}
        </NavLink>

        <span className="nav-section-label" style={{ marginTop: 8 }}>Account</span>
        <NavLink to="/settings" onClick={handleNavClick} className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
          <Settings size={15} /> Settings
        </NavLink>
        <NavLink to="/billing" onClick={handleNavClick} className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
          <CreditCard size={15} /> Billing
        </NavLink>
      </nav>

      {/* AI status */}
      <div className="sidebar-status">
        <div className="flex-center gap-2 mb-1">
          <Cpu size={11} color="var(--text-accent)" />
          <span style={{ color: 'var(--text-accent)', fontSize: 11, fontFamily: 'var(--font-mono)' }}>
            {appStatus?.ai_provider?.toUpperCase() || '—'}
          </span>
          <span style={{
            marginLeft: 'auto', fontSize: 9, fontFamily: 'var(--font-mono)',
            padding: '1px 6px', borderRadius: 8,
            background: appStatus?.ai_ok ? 'rgba(16,217,138,0.12)' : 'rgba(244,63,94,0.12)',
            color: appStatus?.ai_ok ? 'var(--green)' : 'var(--red)',
          }}>
            {appStatus?.ai_ok ? 'ON' : 'OFF'}
          </span>
        </div>
        <div style={{ color: 'var(--text-muted)', fontSize: 10, fontFamily: 'var(--font-mono)' }}>
          {appStatus?.ai_model || '—'}
        </div>
        {appStatus?.trade_count != null && (
          <div style={{ color: 'var(--text-muted)', fontSize: 10, marginTop: 4 }}>
            {appStatus.trade_count} trades stored
          </div>
        )}
      </div>

      {/* User section */}
      <div style={{ borderTop: '1px solid var(--border)', padding: '8px' }}>
        <div
          onClick={() => setShowUserMenu(v => !v)}
          style={{
            display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px',
            borderRadius: 'var(--radius)', cursor: 'pointer', transition: 'background 0.15s',
            background: showUserMenu ? 'var(--bg-hover)' : 'transparent',
          }}
          onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
          onMouseLeave={e => e.currentTarget.style.background = showUserMenu ? 'var(--bg-hover)' : 'transparent'}
        >
          <div style={{
            width: 28, height: 28, borderRadius: '50%',
            background: 'var(--bg-elevated)', border: '1px solid var(--border-bright)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--text-accent)', flexShrink: 0,
          }}>
            {user?.email?.[0]?.toUpperCase() || 'U'}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontSize: 12, fontWeight: 500, color: 'var(--text-primary)',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {user?.full_name || user?.email}
            </div>
            <span style={{
              fontSize: 9, padding: '1px 5px', borderRadius: 5,
              fontFamily: 'var(--font-mono)', fontWeight: 700,
              background: badge.bg, color: badge.color,
            }}>
              {badge.label}
            </span>
          </div>
          <ChevronDown size={12} color="var(--text-muted)"
            style={{ transform: showUserMenu ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
        </div>

        {showUserMenu && (
          <div style={{
            marginTop: 4, padding: '4px',
            background: 'var(--bg-elevated)', border: '1px solid var(--border)',
            borderRadius: 'var(--radius)',
          }}>
            {[
              { icon: <User size={12} />,       label: 'Profile', to: '/profile' },
              { icon: <CreditCard size={12} />, label: 'Billing', to: '/billing' },
            ].map(item => (
              <NavLink key={item.to} to={item.to} onClick={() => { setShowUserMenu(false); handleNavClick() }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '7px 10px', borderRadius: 'var(--radius)',
                  textDecoration: 'none', color: 'var(--text-secondary)',
                  fontSize: 13, transition: 'background 0.15s',
                }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                {item.icon} {item.label}
              </NavLink>
            ))}
            <div style={{ height: 1, background: 'var(--border)', margin: '4px 0' }} />
            <button onClick={logout} style={{
              display: 'flex', alignItems: 'center', gap: 8, width: '100%',
              padding: '7px 10px', border: 'none', background: 'transparent', cursor: 'pointer',
              color: 'var(--red)', fontSize: 13, borderRadius: 'var(--radius)',
              fontFamily: 'var(--font-sans)', transition: 'background 0.15s',
            }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              <LogOut size={12} /> Sign Out
            </button>
          </div>
        )}
      </div>
    </aside>
  )
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth()
  if (loading) return (
    <div style={{
      height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'var(--bg-void)',
    }}>
      <div className="spinner" style={{ width: 24, height: 24 }} />
    </div>
  )
  if (!user) return <Navigate to="/login" replace />
  return children
}

function RouteLoading() {
  return (
    <div className="loading-overlay" style={{ height: '100vh' }}>
      <div className="spinner" />
      <span>Loading page…</span>
    </div>
  )
}

function AppInner() {
  const [appStatus, setAppStatus] = useState(null)
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const { user } = useAuth()

  const refreshStatus = () => getStatus().then(r => setAppStatus(r.data)).catch(() => {})

  useEffect(() => {
    if (user) refreshStatus()
  }, [user])

  return (
    <div className="app-shell">
      {user && (
        <>
          <Sidebar appStatus={appStatus} isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
          {sidebarOpen && <div className="sidebar-backdrop" onClick={() => setSidebarOpen(false)} />}
        </>
      )}
      <main className="main-content">
        {user && (
          <div className="mobile-header">
            <button className="hamburger-btn" onClick={() => setSidebarOpen(true)} aria-label="Open menu">
              <Menu size={20} />
            </button>
            <EdgelogbookLogo size="sm" />
          </div>
        )}
        <Suspense fallback={<RouteLoading />}>
          <Routes>
            <Route path="/login"      element={<LoginPage />} />
            <Route path="/register"   element={<RegisterPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/"           element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard"  element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/trades"     element={<ProtectedRoute><Trades /></ProtectedRoute>} />
            <Route path="/trades/:id" element={<ProtectedRoute><TradeDetail /></ProtectedRoute>} />
            <Route path="/analytics"  element={<ProtectedRoute><Analytics /></ProtectedRoute>} />
            <Route path="/ai"         element={<ProtectedRoute><AICoach /></ProtectedRoute>} />
            <Route path="/import"     element={<ProtectedRoute><ImportPage /></ProtectedRoute>} />
            <Route path="/sync"       element={<ProtectedRoute><SyncPage /></ProtectedRoute>} />
            <Route path="/billing"    element={<ProtectedRoute><BillingPage /></ProtectedRoute>} />
            <Route path="/profile"    element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
            <Route path="/settings"   element={
              <ProtectedRoute>
                <SettingsPage onStatusChange={refreshStatus} />
              </ProtectedRoute>
            } />
          </Routes>
        </Suspense>
      </main>
    </div>
  )
}

export default function App() {
  return (
    <BrowserRouter>
      <Toaster position="top-right" toastOptions={{
        style: {
          background: 'var(--bg-elevated)', color: 'var(--text-primary)',
          border: '1px solid var(--border)', fontSize: 13,
        }
      }} />
      <AuthProvider>
        <AccountProvider>
          <AppInner />
        </AccountProvider>
      </AuthProvider>
    </BrowserRouter>
  )
}
